import cookies from 'js-cookie';
import type { Router } from 'next/router';
import { buildUrl } from 'Routes/routeBuilder';
import { PAYMENT_STATUS_FAILURE, PAYMENT_STATUS_SUCCESS } from 'Utils/payment/constants';
import { STATUS_CODE_200, STATUS_CODE_400 } from 'Utils/status-code/statusCode.constants';
import { redirectToUri } from 'Utils/uri/redirectUtils';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { PROPOSAL_UPDATE } from 'Utils/request/requestDictionary';
import { isStrictlyDevelopmentEnv } from 'Utils/context/contextUtils';
import { trackDelCredereBanking } from 'Views/booking/BookingEngine.tracking';
import {
  WRONG_SALESMAN_ID,
  SALESMAN_ID_WARNING_MESSAGE,
} from 'Components/widgets/contact-info/constants';
type Args = {
  appContext: AppContext;
  router: Router;
  proposal: Proposal;
  salesmanID?: string | null | undefined;
};

const getQuery = (proposal, appContext, salesmanID) => `
mutation {
  bookings(
    proposalId: "${proposal?.id || ''}"
    bookingStatus: VALIDATED
    ${appContext.cmta.email ? `sellerEmail: "${appContext.cmta.email}"` : ''}
    ${salesmanID ? `salesmanID: "${salesmanID}"` : ''}
  ) {
    bookingId
  }
}
`;

export const submitDelCredereBooking = ({ appContext, router, proposal, salesmanID }: Args) => {
  trackDelCredereBanking(
    {
      labelGA: 'clickValidateBanking',
      paymentMethod: 'Ducroire',
      orderID_PSP: null,
    },
    appContext,
  );
  cookies.set(
    `${SALESMAN_ID_WARNING_MESSAGE}-${proposal.id}`,
    salesmanID === '' ? 'show' : 'hide',
    {
      secure: !isStrictlyDevelopmentEnv(),
    },
  );
  const queryWithSalesmanID = getQuery(proposal, appContext, salesmanID);
  const queryWithoutSalesmanID = getQuery(proposal, appContext, undefined);
  const redirect = redirectToUri(router);

  const handleSuccess = (response) => {
    trackDelCredereBanking(
      {
        labelGA: 'validateBanking',
        paymentPlan: 'full payment',
      },
      appContext,
    );
    const url = buildUrl(
      'confirmation',
      {
        status: STATUS_CODE_200,
      },
      {
        ...router.query,
        selectedProposalId: proposal.id,
        bi: response?.bookings?.bookingId || '',
        paymentStatus: PAYMENT_STATUS_SUCCESS,
      },
    );
    redirect(url)();
  };

  const handleError = (error) => {
    trackDelCredereBanking(
      {
        labelGA: 'errorValidateBanking',
        paymentPlan: 'full payment',
      },
      appContext,
    );
    const status = error?.errors?.[0]?.responseBody?.status_code || STATUS_CODE_400;
    const error_code =
      error?.errors?.[0]?.responseBody?.errors?.[0]?.error_code || 'BOOKING_UNAVAILABLE';
    const error_description = error?.errors?.[0]?.responseBody?.errors?.[0]?.error_description;

    if (status === STATUS_CODE_400 && error_code === WRONG_SALESMAN_ID) {
      cookies.set(`${SALESMAN_ID_WARNING_MESSAGE}-${proposal.id}`, 'show', {
        secure: !isStrictlyDevelopmentEnv(),
      });
      return graphQLClient
        .runNoCacheQuery(queryWithoutSalesmanID, appContext, {
          requestName: PROPOSAL_UPDATE,
        })
        .then(handleSuccess)
        .catch(handleError);
    }

    const url = buildUrl(
      'confirmation',
      {
        error_code,
        error_description,
        status,
      },
      { ...router.query, selectedProposalId: proposal.id, paymentStatus: PAYMENT_STATUS_FAILURE },
    );
    redirect(url)();
  };

  return graphQLClient
    .runNoCacheQuery(queryWithSalesmanID, appContext, {
      requestName: PROPOSAL_UPDATE,
    })
    .then(handleSuccess)
    .catch(handleError);
};
